import { PublicKey } from '@solana/web3.js'
import { Wallet } from '@solana/wallet-adapter-react'
import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useQuery } from '@tanstack/react-query'
import { abbreviateAddress } from 'utils/formatting'
import { useViewport } from 'hooks/useViewport'
import { TV_USER_ID_KEY } from 'utils/constants'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import WalletDrawer from './WalletDrawer'
import WalletIcon from '@components/icons/WalletIcon'
import { useOrdersCount } from 'hooks/useOrdersCount'
import ProfileImage from '@components/profile/ProfileImage'
import { useProfileDetails } from '@components/profile/useProfileDetails'
import { notify } from 'utils/notifications'
import { WelcomeResponse } from 'pages/api/intern/welcome'

const ConnectedMenu = ({
  publicKey,
  wallet,
}: {
  publicKey: PublicKey
  wallet: Wallet
}) => {
  const { t, ready } = useTranslation('topbar')
  const { isDesktop, isMobile } = useViewport()
  const [tvUserId, setTvUserId] = useLocalStorage(TV_USER_ID_KEY)
  const [showDraw, setShowDraw] = useState(false)
  const { ordersCount } = useOrdersCount()
  const { profileDetails, isFetchingProfileDetails } = useProfileDetails()
  const [wasWelcomed, setWasWelcomed] = useState(false)

  const { data: message, isInitialLoading: isInitialLoadingMessage } = useQuery(
    {
      queryKey: ['welcomeMessage'],
      queryFn: async () => {
        const response = await fetch(`/api/intern/welcome?ts=${Date.now()}`)
        const data = (await response.json()) as WelcomeResponse
        return data.message
      },
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: !isFetchingProfileDetails && !wasWelcomed,
    },
  )

  // It will pop up twice on localhost on prod won't, its due to react strict mode
  useEffect(() => {
    if (
      !ready ||
      isFetchingProfileDetails ||
      isInitialLoadingMessage ||
      wasWelcomed
    ) {
      return
    }

    if (!isMobile) {
      setWasWelcomed(true)
      notify({
        title:
          t('welcome-message.greeting', {
            username:
              profileDetails?.username ||
              abbreviateAddress(publicKey.toBase58()),
          }) +
          (profileDetails?.username
            ? ` ${t('welcome-message.see-again')}`
            : ''),
        description: message,
        type: 'success',
      })
    }
  }, [
    isFetchingProfileDetails,
    isInitialLoadingMessage,
    message,
    profileDetails?.username,
    publicKey,
    ready,
    t,
    wasWelcomed,
    isMobile,
  ])

  const toggleModal = useCallback(() => {
    setShowDraw(!showDraw)
  }, [showDraw])

  useEffect(() => {
    if (!tvUserId) {
      setTvUserId(publicKey.toString())
    }
  }, [tvUserId, setTvUserId, publicKey])

  return (
    <>
      <button
        className={clsx(
          'default-transition relative h-10 rounded-full bg-th-bkg-3 sm:h-12 md:hover:bg-th-bkg-4',
          isDesktop ? 'w-40 px-2.5' : 'w-10 sm:w-12',
          'hover:bg-th-bkg-2 focus:outline-none focus-visible:bg-th-bkg-3',
        )}
        onClick={toggleModal}
      >
        {ordersCount ? (
          <div className="absolute -top-1 right-0 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-th-active px-1">
            <span className="font-mono text-[10px] font-semibold text-th-fgd-1">
              {ordersCount}
            </span>
          </div>
        ) : null}
        <div className="flex items-center justify-center lg:justify-start">
          {profileDetails?.hasImage ? (
            <ProfileImage
              imageSize={isDesktop ? 32 : isMobile ? 40 : 48}
              placeholderSize="20"
            />
          ) : (
            <div className="flex size-7 items-center justify-center rounded-full bg-th-bkg-2 sm:size-8">
              <WalletIcon className="size-4 text-th-fgd-3 sm:size-5" />
            </div>
          )}
          {isDesktop ? (
            <div className="ml-2 overflow-hidden text-left">
              <p className="mb-1 text-xs leading-none text-th-fgd-3">
                {profileDetails?.username || wallet.adapter.name}
              </p>
              <p className="truncate pr-2 text-xs font-bold leading-none text-th-fgd-1">
                {abbreviateAddress(publicKey)}
              </p>
            </div>
          ) : null}
        </div>
      </button>
      <WalletDrawer
        publicKey={publicKey}
        wallet={wallet}
        isOpen={showDraw}
        onClose={toggleModal}
      />
    </>
  )
}

export default ConnectedMenu
